import type { CatalogQuota, QuotaShortfall } from '@/api/types'
import { quotaSentence } from '@/domain/errors'
import { quotaMemoryText, quotaSizeText, quotaText } from '@/domain/quota'
import { cn } from '@/lib/utils'
import { size, sizeMb } from '@/ui/format'

type Axis = QuotaShortfall['axis']

/**
 * 이 신청이 내 쿼터에 더하는 양. 축마다 신청이 받아들여진 뒤의 쓰는 양/한도와 더하는 양이고, 넘는 축이 있으면 그 축이 막힘 문장이 된다 —
 * 서버의 쿼터 거절과 같은 문장(`quotaSentence`)이다. 판정은 서버의 것이라 버튼을 잠그지 않는다.
 * `waived` 는 서버가 묻지 않는 축이다 — 카드를 실은 신청의 메모리(F-101). 넘어도 막힘으로 칠하지 않는다.
 * `axes` 는 서는 축이다 — VM 생성은 넷 다, 변경 신청은 코어와 메모리뿐이다(변경은 VM 수도 디스크도 바꾸지 않는다).
 * VM 생성 페이지와 변경 신청 창이 같은 상자를 쓴다.
 */
export function QuotaBox({
  quota: q,
  add,
  waived = [],
  axes = ['vms', 'cores', 'memory_mb', 'disk_gb'],
}: {
  quota: CatalogQuota
  add: Partial<Record<Axis, number>>
  waived?: Axis[]
  axes?: Axis[]
}) {
  const plus = (a: Axis) => add[a] ?? 0
  const all: { axis: Axis; word: string; used: number; limit: number | null; after: string; plus: string }[] = [
    { axis: 'vms', word: 'VM', used: q.used.max_vms, limit: q.limits.max_vms, after: quotaText(q.used.max_vms + plus('vms'), q.limits.max_vms, '대'), plus: `+${plus('vms')}대` },
    { axis: 'cores', word: '코어', used: q.used.max_cores, limit: q.limits.max_cores, after: quotaText(q.used.max_cores + plus('cores'), q.limits.max_cores), plus: `+${plus('cores')}` },
    {
      axis: 'memory_mb',
      word: '메모리',
      used: q.used.max_memory_mb,
      limit: q.limits.max_memory_mb,
      after: quotaMemoryText(q.used.max_memory_mb + plus('memory_mb'), q.limits.max_memory_mb),
      plus: `+${sizeMb(plus('memory_mb'))}`,
    },
    {
      axis: 'disk_gb',
      word: '디스크',
      used: q.used.max_disk_gb,
      limit: q.limits.max_disk_gb,
      after: quotaSizeText(q.used.max_disk_gb + plus('disk_gb'), q.limits.max_disk_gb),
      plus: `+${size(plus('disk_gb'))}`,
    },
  ]
  const rows = all.filter((r) => axes.includes(r.axis))
  const over = (r: (typeof rows)[number]) => !waived.includes(r.axis) && plus(r.axis) > 0 && r.limit !== null && r.used + plus(r.axis) > r.limit
  const first = rows.find(over)
  const shortfall: QuotaShortfall | null =
    first && first.limit !== null ? { axis: first.axis, limit: first.limit, used: first.used, asked: plus(first.axis) } : null
  return (
    <div className="rounded-lg border bg-surface-2/60 px-3 py-2">
      <div className="mb-1.5 text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">내 쿼터</div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12px]">
        {rows.map((r) => (
          <div key={r.axis} className="contents">
            <dt className="text-muted-foreground">{r.word}</dt>
            <dd className={cn('min-w-0 text-right tabular-nums', over(r) && 'text-danger-ink')}>
              {r.after}
              {plus(r.axis) > 0 ? <span className="ml-1.5 text-muted-foreground">{r.plus}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
      {shortfall ? <p className="mt-2 text-[12px] text-danger-ink">{quotaSentence(shortfall)}</p> : null}
    </div>
  )
}
